import {showNotification, Switch} from 'platform/components';
import {Box, Heading, Hide, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {equals, isNotEmpty} from 'ramda';

import {
  PhotoGuideSetting,
  PhotoGuideSlot,
  SlotSaleVehiclePhotoType,
  useUpdatePhotoGuideSettingMutation,
} from '@dms/api/settings';
import {SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError, usePermissions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {PhotoGuideSlotRow} from './PhotoGuideSlotRow';
import {PhotoTypePicker} from './PhotoTypePicker';

const CONTAINER_ID = 'photo-guide-slots';

type SortableSlot = PhotoGuideSlot & {id: SlotSaleVehiclePhotoType};

interface PhotoGuideFormProps extends RequiredTestIdProps {
  saved: PhotoGuideSetting;
  isRefreshing: boolean;
}

/**
 * Edits the tenant's Photo Guide as a local draft; one Save sends the whole guide. Without the
 * configure permission the guide is shown read-only.
 */
export function PhotoGuideForm(props: PhotoGuideFormProps) {
  const [canConfigure] = usePermissions({permissionKeys: ['configurePhotoGuide']});
  const [draft, setDraft] = useState<PhotoGuideSetting>(props.saved);
  const [updatePhotoGuide, {isLoading: isSaving}] = useUpdatePhotoGuideSettingMutation();

  const isReadOnly = !canConfigure;
  // The refetch after a save remounts the form, which would drop edits made until it finishes.
  const isBusy = isSaving || props.isRefreshing;
  const isLocked = isReadOnly || isBusy;
  const isDirty = !equals(draft, props.saved);
  const sortableSlots: SortableSlot[] = draft.slots.map((slot) => ({...slot, id: slot.photoType}));
  const hasSlots = isNotEmpty(sortableSlots);

  const setSlots = (slots: PhotoGuideSlot[]) => setDraft({...draft, slots});

  const setRequired = (photoType: SlotSaleVehiclePhotoType, required: boolean) =>
    setSlots(
      draft.slots.map((slot) => (slot.photoType === photoType ? {...slot, required} : slot))
    );

  const removeSlot = (photoType: SlotSaleVehiclePhotoType) =>
    setSlots(draft.slots.filter((slot) => slot.photoType !== photoType));

  const addSlot = (photoType: SlotSaleVehiclePhotoType) =>
    setSlots([...draft.slots, {photoType, required: false}]);

  const handleSave = () =>
    updatePhotoGuide(draft)
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);

  return (
    <SettingsSection>
      <VStack spacing={6}>
        <Switch
          label={i18n.t('page.salesSettings.photoGuide.labels.enabled')}
          value={draft.enabled}
          isDisabled={isLocked}
          onChange={(enabled) => setDraft({...draft, enabled})}
          data-testid={suffixTestId('enabled', props)}
        />

        <VStack spacing={4} align="flex-start">
          <VStack spacing={1}>
            <Heading size={4}>{i18n.t('page.salesSettings.photoGuide.labels.slots')}</Heading>
            <Text size="small" color="secondary">
              {i18n.t('page.salesSettings.photoGuide.labels.slotsHint')}
            </Text>
          </VStack>

          <Hide when={hasSlots}>
            <Text size="small" color="tertiary" data-testid={suffixTestId('noSlots', props)}>
              {i18n.t('page.salesSettings.photoGuide.labels.noSlots')}
            </Text>
          </Hide>
          <Show when={hasSlots}>
            <Box width="100%">
              <SortableCardList<SortableSlot>
                containerId={CONTAINER_ID}
                items={sortableSlots}
                withHandle
                disableDropAnimation
                disabledDrag={isLocked}
                onReorder={(items) =>
                  setSlots(
                    items.map((item) => ({photoType: item.photoType, required: item.required}))
                  )
                }
                renderCard={(slot, index, handleProps) => (
                  <PhotoGuideSlotRow
                    slot={slot}
                    position={index + 1}
                    handleProps={handleProps}
                    isReadOnly={isReadOnly}
                    isDisabled={isLocked}
                    onRequiredChange={(required) => setRequired(slot.photoType, required)}
                    onRemove={() => removeSlot(slot.photoType)}
                    data-testid={suffixTestId(`slot-${slot.photoType}`, props)}
                  />
                )}
              />
            </Box>
          </Show>

          <PhotoTypePicker
            usedPhotoTypes={draft.slots.map((slot) => slot.photoType)}
            isDisabled={isLocked}
            onSelect={addSlot}
            data-testid={suffixTestId('addSlot', props)}
          />
        </VStack>
      </VStack>

      <Show when={canConfigure}>
        <SettingsFooter
          actions={[
            {
              type: 'button',
              variant: 'secondary',
              title: i18n.t('general.actions.discardChanges'),
              isDisabled: !isDirty || isBusy,
              onClick: () => setDraft(props.saved),
              'data-testid': suffixTestId('discard', props),
            },
            {
              type: 'button',
              variant: 'primary',
              title: i18n.t('general.actions.save'),
              isDisabled: !isDirty || isBusy,
              isLoading: isBusy,
              onClick: handleSave,
              'data-testid': suffixTestId('save', props),
            },
          ]}
          data-testid={props['data-testid']}
        />
      </Show>
    </SettingsSection>
  );
}
